/**
 * Human labels for URL segments, and the title they add up to.
 *
 * Lifted out of Breadcrumb so the browser tab and the breadcrumb cannot drift
 * apart: both now read the same map. Anything not listed falls back to a
 * title-cased segment, which is why an unfamiliar route still gets a sensible
 * name rather than nothing.
 */
export const SEGMENT_LABELS: Record<string, string> = {
  // Top-level
  map: 'Map',
  incidents: 'Incidents',
  tasks: 'Tasks',
  forms: 'Forms',
  board: 'Board',
  shelters: 'Shelters',
  contacts: 'Contacts',
  inventory: 'Inventory',
  settings: 'Settings',
  admin: 'Admin',
  weather: 'Weather',
  notifications: 'Notifications',
  businesses: 'Businesses',
  reports: 'Reports',
  team: 'Team',
  account: 'Account',
  help: 'Help Center',
  files: 'File Library',
  dashboard: 'Dashboard',
  dashboards: 'EOC Displays',

  // Standalone pages that sit outside the main shell. Without these the
  // fallback title-cases the URL segment, which reads as an accident.
  onboarding: 'Set up your account',
  invitations: 'Accept invitation',
  'setup-account': 'Set up your account',
  stream: 'Live stream',
  display: 'Display',
  callback: 'Signing in',
  'verify-email': 'Verify email',
  unsubscribe: 'Unsubscribe',
  orthophoto: 'Orthophoto',
  safety: 'Safety',
  logistics: 'Logistics',

  // Response
  'call-center': 'Call Center',
  ics214: 'Activity Logs',
  lifelines: 'Community Lifelines',
  pio: 'PIO',
  statements: 'Statements',
  'media-contacts': 'Media Contacts',

  // Situational awareness
  'damage-assessments': 'Damage Assessments',
  'community-reports': 'Community Reports',
  submissions: 'Submissions',
  'public-submissions': 'Public Submissions',
  hazmat: 'HAZMAT',
  infrastructure: 'Infrastructure',
  'road-closures': 'Road Closures',
  'utility-outages': 'Utility Outages',
  critical: 'Critical Infrastructure',

  // People & Credentials
  credentials: 'Credentials',
  review: 'Review',
  agencies: 'Agencies',
  'personnel-tracking': 'Personnel Tracking',
  'at-risk-registry': 'At Risk Registry',

  // Shelters & Evacuation
  pods: 'Points of Distribution',
  evacuation: 'Evacuation Zones',

  // Logistics
  'supply-requests': 'Supply Requests',
  'mutual-aid': 'Mutual Aid',
  agreements: 'Agreements',
  requests: 'Requests',
  costs: 'Cost Tracker',
  procurement: 'Procurement',

  // Recovery & Planning
  coop: 'COOP',
  plans: 'Plans',
  activations: 'Activations',
  'business-continuity': 'Business Continuity',
  sectors: 'Sectors',
  'after-action-reviews': 'After Action Reviews',
  recovery: 'Recovery Dashboard',
  'fema-reimbursement': 'FEMA Reimbursement',
  worksheets: 'Worksheets',
  'ia-cases': 'IA Cases',
  appeals: 'Appeals',
  'audit-packages': 'Audit Packages',
  export: 'Export',
  rates: 'Rate Management',
  closeout: 'Closeout',

  // Wildfire
  wildfire: 'Fire Perimeters',
  'fire-behavior': 'Fire Behavior',
  'smoke-tracking': 'Smoke Tracking',

  // sUAS
  suas: 'sUAS',
  drones: 'Drones',
  operators: 'Operators',
  missions: 'Missions',
  fleet: 'Fleet',
  compliance: 'Compliance',
  analytics: 'Analytics',
  'flight-logs': 'Flight Logs',
  pilots: 'Pilot Performance',
  devices: 'Devices',
  livestream: 'Live Feeds',
  operations: 'Operations',
  authorizations: 'Authorizations',
  maintenance: 'Maintenance',
  analysis: 'Analysis',
  snapshots: 'Snapshot Analysis',
  'search-patterns': 'Search Patterns',
  pointcloud: 'Point Cloud',

  // Notifications & automation
  'ipaws': 'IPAWS',
  alerts: 'Alerts',
  compose: 'Compose',
  automations: 'Automations',
  runs: 'Run History',

  // Settings sub-pages
  organization: 'Organization',
  operational: 'Operational',
  appearance: 'Appearance',
  branding: 'Branding',
  roles: 'Roles',
  positions: 'Positions',
  templates: 'Templates',
  'notification-templates': 'Notification Templates',
  'task-types': 'Task Types',
  protocols: 'Protocols',
  'quick-actions': 'Quick Actions',
  portal: 'Portal',
  'eoc-displays': 'EOC Displays',
  'pda-zones': 'PDA Zones',
  'infrastructure-types': 'Infrastructure Types',
  'resource-types': 'Resource Types',
  integrations: 'Integrations',
  'data-export': 'Data Export',

  // Misc
  'audit-logs': 'Audit Logs',
  geofences: 'Geofences',
  'my-notifications': 'My Notifications',
  'flood-history': 'Flood Forecast History',
  new: 'New',
  edit: 'Edit',
  monitor: 'Monitor',
  view: 'View',
  import: 'Import',
  ledger: 'Ledger',
  locations: 'Locations',
  folder: 'Folder',

  // Field
  field: 'Field Ops',
  damage: 'Damage',
  roads: 'Roads',
  zones: 'Zones',
  setup: 'Setup',
  manage: 'Manage',
  supplies: 'Supplies',
  'shelter-checkin': 'Shelter Check-in',
};

/** Routes where breadcrumbs add no value. Each entry hides the exact route and any sub-route. */
export const HIDDEN_PATTERNS = [
  '/',             // root — home icon already represents this
  '/login',
  '/callback',
  '/map',          // full-screen map workspace
  '/call-center',  // call-taker workspace
  '/field',        // field ops workspace
];

export function isHiddenRoute(pathname: string): boolean {
  return HIDDEN_PATTERNS.some(
    (pattern) => pathname === pattern || pathname.startsWith(pattern + '/'),
  );
}

/** Check if a segment looks like a UUID */
export function isUuid(segment: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
}


/** The label for a single URL segment. */
export function labelForSegment(segment: string): string {
  return (
    SEGMENT_LABELS[segment] ||
    segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
  );
}

/**
 * A page name for a pathname — the deepest named segment.
 *
 * Record ids are skipped: "/incidents/8f2c…/tasks" is "Tasks", not a UUID. A
 * detail route whose last segment IS the id falls back to the segment above it,
 * so "/shelters/8f2c…" reads "Shelters" rather than going blank.
 */
/**
 * Segments that describe an action rather than a place. On their own they name
 * nothing: every create route in the app ends in one, so `/inventory/new`,
 * `/coop/plans/new` and `/reports/new` all produced the title "New" — the same
 * collision UI-60 set out to remove, reintroduced one level down.
 */
const GENERIC_SEGMENTS = new Set(['new', 'edit', 'create', 'add', 'duplicate', 'clone']);

/**
 * Segments whose child is a secret, not a name.
 *
 * `isUuid` already drops record ids, but share and invitation links carry
 * opaque tokens that are not UUIDs -- so `/display/reports/aG9sZDEy` title-cased
 * into "Ag9zZDEy" and put it in the browser tab. Worse than unnamed: it puts a
 * capability token in the tab title, the window switcher, and the history entry
 * the recipient may later screenshot or screen-share.
 */
const TOKEN_PARENTS = new Set([
  'invitations',
  'setup-account',
  'verify-email',
  'unsubscribe',
  'stream',
]);

/**
 * Two-segment tails whose child is a token.
 *
 * `reports` and `share` cannot go in the set above: `/reports/new` is an
 * ordinary route, and dropping its trailing segment turned "Reports — New" into
 * "Reports". Only the token-bearing paths qualify, named in full.
 */
const TOKEN_TAILS = new Set(['display/reports', 'orthophoto/share']);

/**
 * Two-segment tails whose combined meaning is not the sum of their labels.
 * "Reports" and "Share" are both true and neither tells you what you are
 * looking at.
 */
const PATH_LABELS: Record<string, string> = {
  'display/reports': 'Shared report',
  'orthophoto/share': 'Shared orthophoto',
};

export function pageNameForPath(pathname: string): string {
  const named = pathname
    .split('/')
    .filter(Boolean)
    .filter((seg) => !isUuid(seg));
  if (named.length === 0) return 'Overview';

  // Drop a trailing token so it never reaches a title.
  const parentSeg = named.length >= 2 ? named[named.length - 2].toLowerCase() : '';
  const grandTail = named.length >= 3 ? `${named[named.length - 3]}/${parentSeg}`.toLowerCase() : '';
  if (parentSeg && (TOKEN_PARENTS.has(parentSeg) || TOKEN_TAILS.has(grandTail))) {
    named.pop();
  }
  if (named.length === 0) return 'Overview';

  const tail = named.slice(-2).join('/').toLowerCase();
  if (PATH_LABELS[tail]) return PATH_LABELS[tail];

  const last = named[named.length - 1];
  const parent = named[named.length - 2];
  if (GENERIC_SEGMENTS.has(last.toLowerCase()) && parent) {
    // "Inventory — New" rather than "New". Parent first, because that is the
    // half that tells two create tabs apart; and joined rather than inflected,
    // because singularising "Analyses" or "Statuses" correctly is not worth a
    // page title.
    return `${labelForSegment(parent)} — ${labelForSegment(last)}`;
  }
  return labelForSegment(last);
}

/** What belongs in `document.title` for a pathname. */
export function documentTitleForPath(pathname: string, appName = 'Command Bridge'): string {
  const page = pageNameForPath(pathname);

  // Do not stutter. The tenant root is "Overview", and the admin console's root
  // segment is "admin" against an app name of "Command Bridge Admin" — prefixing
  // either gives "Admin · Command Bridge Admin", which reads like a bug because
  // it is one.
  if (page === 'Overview') return appName;
  if (appName.toLowerCase().includes(page.toLowerCase())) return appName;

  return `${page} · ${appName}`;
}
